import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";

import { useDataStore } from "../dataStore";
import { toMediaUrl, withImageSize } from "../api";
import {
  brandThemeCssVars,
  DEFAULT_BRAND_THEME,
} from "../themeTokens";
import BrandedImageFallback from "../components/BrandedImageFallback";
import OptimizedConfigImage from "../components/OptimizedConfigImage";
import PageStateCard from "../components/PageStateCard";

const safeText = (value) => String(value || "").trim();

function isExplicitlyDisabled(value) {
  if (value === false || value === 0) return true;
  return ["false", "0", "off", "no"].includes(safeText(value).toLowerCase());
}

function getConfiguredImage(value) {
  const image = safeText(value);
  return image ? toMediaUrl(image) : "";
}

const PHONE_SCREEN_ASPECT_RATIO = "9 / 19.5";

function HomeAppScreen({ src, alt }) {
  const [usesWideCanvas, setUsesWideCanvas] = useState(false);

  useEffect(() => {
    setUsesWideCanvas(false);
  }, [src]);

  return (
    <Box
      data-testid="home-app-screen"
      style={{ aspectRatio: PHONE_SCREEN_ASPECT_RATIO }}
      sx={{
        position: "absolute",
        width: { xs: 220, sm: 260, md: 300 },
        maxWidth: "72vw",
        left: "50%",
        bottom: { xs: -125, md: -150 },
        overflow: "hidden",
        borderRadius: "17% / 8%",
        transform: "translateX(-50%)",
        filter: "drop-shadow(0 30px 54px rgba(0,0,0,0.42))",
      }}
    >
      <Box
        component={OptimizedConfigImage}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={(event) => {
          const { naturalWidth, naturalHeight } = event.currentTarget;
          setUsesWideCanvas(
            Boolean(naturalHeight && naturalWidth / naturalHeight >= 1)
          );
        }}
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          maxWidth: "none",
          objectFit: usesWideCanvas ? "cover" : "contain",
          objectPosition: "center",
        }}
      />
    </Box>
  );
}

function getMainImage(entity, relationKey) {
  const images = Array.isArray(entity?.[relationKey])
    ? entity[relationKey]
    : [];
  const main = images.find((image) => image?.isMain) || images[0];
  return main?.image ? toMediaUrl(main.image) : "";
}

function getCategoryImage(category) {
  return category?.image ? toMediaUrl(category.image) : "";
}

function getPriority(entity) {
  return Number.isFinite(Number(entity?.priority))
    ? Number(entity.priority)
    : 999999;
}

function compareNames(a, b) {
  return safeText(a).localeCompare(safeText(b), undefined, {
    sensitivity: "base",
  });
}

function getAddressLine(location) {
  const city = safeText(location?.address?.city?.name);
  const street = safeText(location?.address?.street);
  const number = safeText(location?.address?.number);
  const streetLine = [street, number].filter(Boolean).join(" ");
  return [city, streetLine].filter(Boolean).join(", ");
}

function getConfiguredAction(labelValue, urlValue) {
  const label = safeText(labelValue);
  const rawUrl = safeText(urlValue);

  if (!label || !rawUrl || /\s/.test(rawUrl) || rawUrl.includes("\\")) {
    return null;
  }

  if (rawUrl.startsWith("/") && !rawUrl.startsWith("//")) {
    return { label, to: rawUrl, external: false };
  }

  if (/^#[a-z0-9][a-z0-9:._-]*$/i.test(rawUrl)) {
    return { label, href: rawUrl, external: false };
  }

  try {
    if (new URL(rawUrl).protocol === "https:") {
      return { label, href: rawUrl, external: true };
    }
  } catch {
    return null;
  }

  return null;
}

function ActionButton({
  action,
  variant = "contained",
  color = "primary",
  sx = {},
}) {
  if (!action) return null;

  const linkProps = action.to
    ? { component: RouterLink, to: action.to }
    : {
        component: "a",
        href: action.href,
        ...(action.external
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {}),
      };

  return (
    <Button
      {...linkProps}
      variant={variant}
      color={color}
      endIcon={
        action.external ? (
          <OpenInNewRoundedIcon fontSize="small" />
        ) : (
          <ArrowForwardRoundedIcon fontSize="small" />
        )
      }
      sx={{
        minHeight: 46,
        borderRadius: "12px",
        px: 2.5,
        fontWeight: 900,
        textTransform: "none",
        ...sx,
      }}
    >
      {action.label}
    </Button>
  );
}

function SectionHeading({
  id,
  title,
  body,
  actionLabel,
  actionTo,
  color,
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "flex-start", sm: "flex-end" }}
      justifyContent="space-between"
      spacing={2}
      sx={{ mb: { xs: 2, md: 2.75 } }}
    >
      <Box sx={{ maxWidth: 720 }}>
        <Typography
          component="h2"
          id={id}
          sx={{
            color,
            fontWeight: 950,
            fontSize: { xs: 29, sm: 34, md: 40 },
            lineHeight: 1.08,
            letterSpacing: "-0.025em",
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>

        {body ? (
          <Typography
            color="text.secondary"
            sx={{
              mt: 1,
              maxWidth: 640,
              fontSize: { xs: 15, md: 17 },
              lineHeight: 1.65,
              whiteSpace: "pre-line",
            }}
          >
            {body}
          </Typography>
        ) : null}
      </Box>

      {actionLabel && actionTo ? (
        <Button
          component={RouterLink}
          to={actionTo}
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            minHeight: 44,
            borderRadius: "12px",
            px: 1.5,
            flexShrink: 0,
            color,
            fontWeight: 900,
            textTransform: "none",
          }}
        >
          {actionLabel}
        </Button>
      ) : null}
    </Stack>
  );
}

function HeroCarousel({ slides, brandName, brandTheme, t }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(null);
  const useMobileImage = useMediaQuery("(max-width:599.95px)");
  const c = brandTheme;
  const count = slides.length;

  useEffect(() => {
    setActiveIndex((current) => (current < count ? current : 0));
  }, [count]);

  const showSlide = (index) => {
    if (!count) return;
    setActiveIndex((index + count) % count);
  };

  const activeSlide = slides[activeIndex] || slides[0];
  if (!activeSlide) return null;
  const activeImage =
    (useMobileImage && activeSlide.mobileImage) || activeSlide.image;

  const onTouchStart = (event) => {
    touchStartX.current = event.touches?.[0]?.clientX ?? null;
  };

  const onTouchEnd = (event) => {
    if (touchStartX.current === null || count < 2) return;
    const endX = event.changedTouches?.[0]?.clientX;
    if (!Number.isFinite(endX)) return;

    const distance = endX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(distance) < 48) return;
    showSlide(activeIndex + (distance < 0 ? 1 : -1));
  };

  return (
    <Box
      component="section"
      aria-roledescription="carousel"
      aria-label={t("home.heroCarousel")}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      sx={{
        minHeight: { xs: 500, sm: 550, md: 620 },
        position: "relative",
        display: "flex",
        alignItems: "flex-end",
        overflow: "hidden",
        borderRadius: { xs: "18px", md: "24px" },
        bgcolor: c.dark,
        boxShadow: "0 28px 74px rgba(23,17,12,0.22)",
        touchAction: "pan-y",
      }}
    >
      <Box
        key={activeSlide.slot || activeIndex}
        role="group"
        aria-roledescription="slide"
        aria-label={`${activeIndex + 1} / ${count}`}
        aria-live="polite"
        sx={{
          position: "absolute",
          inset: 0,
          animation: count > 1 ? "hero-reveal 220ms ease-out" : "none",
          "@keyframes hero-reveal": {
            from: { opacity: 0.72 },
            to: { opacity: 1 },
          },
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        {activeImage ? (
          <Box
            component={OptimizedConfigImage}
            src={activeImage}
            alt={activeSlide.imageAlt}
            loading={activeIndex === 0 ? "eager" : "lazy"}
            decoding="async"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(circle at 78% 16%, ${c.primary} 0%, transparent 34%), linear-gradient(135deg, ${c.dark} 0%, ${c.accent} 150%)`,
              opacity: 0.86,
            }}
          />
        )}

        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(16,12,9,0.08) 5%, rgba(16,12,9,0.34) 52%, rgba(16,12,9,0.91) 100%)",
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "flex-end",
            pb: count > 1 ? { xs: 6.5, sm: 5.5 } : 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 880,
              p: { xs: 3, sm: 4.5, md: 6 },
            }}
          >
            {safeText(brandName) &&
            safeText(brandName) !== activeSlide.title ? (
              <Typography
                sx={{
                  mb: 1.25,
                  color: "rgba(255,255,255,0.82)",
                  fontSize: 13,
                  fontWeight: 900,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                {brandName}
              </Typography>
            ) : null}

            <Typography
              component="h1"
              id="home-hero-title"
              sx={{
                maxWidth: 820,
                color: "#ffffff",
                fontWeight: 950,
                fontSize: { xs: 42, sm: 60, md: 76 },
                lineHeight: 0.98,
                letterSpacing: "-0.045em",
                whiteSpace: "pre-line",
                textWrap: "balance",
                overflowWrap: "anywhere",
                textShadow: "0 6px 28px rgba(0,0,0,0.28)",
              }}
            >
              {activeSlide.title}
            </Typography>

            {activeSlide.body ? (
              <Typography
                sx={{
                  mt: 2,
                  maxWidth: 650,
                  color: "rgba(255,255,255,0.88)",
                  fontSize: { xs: 16, md: 19 },
                  lineHeight: 1.62,
                  whiteSpace: "pre-line",
                  textShadow: "0 3px 18px rgba(0,0,0,0.26)",
                }}
              >
                {activeSlide.body}
              </Typography>
            ) : null}

            {activeSlide.primaryAction || activeSlide.secondaryAction ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ mt: 3.25 }}
              >
                <ActionButton
                  action={activeSlide.primaryAction}
                  sx={{
                    color: c.onCta,
                    bgcolor: c.cta,
                    "&:hover": {
                      color: c.onCta,
                      bgcolor: c.cta,
                      filter: "brightness(0.94)",
                    },
                    "&:focus-visible": {
                      outline: `3px solid ${c.primary}`,
                      outlineOffset: 3,
                    },
                  }}
                />

                <ActionButton
                  action={activeSlide.secondaryAction}
                  variant="outlined"
                  sx={{
                    color: "#ffffff",
                    borderColor: "rgba(255,255,255,0.52)",
                    bgcolor: "rgba(15,15,15,0.20)",
                    backdropFilter: "blur(10px)",
                    "&:hover": {
                      color: "#ffffff",
                      borderColor: "#ffffff",
                      bgcolor: "rgba(15,15,15,0.34)",
                    },
                    "&:focus-visible": {
                      outline: `3px solid ${c.primary}`,
                      outlineOffset: 3,
                    },
                  }}
                />
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Box>

      {count > 1 ? (
        <>
          <Button
            type="button"
            aria-label={t("common.previous")}
            onClick={() => showSlide(activeIndex - 1)}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              position: "absolute",
              left: 18,
              top: "50%",
              zIndex: 3,
              minWidth: 46,
              width: 46,
              height: 46,
              p: 0,
              borderRadius: "50%",
              color: "#fff",
              bgcolor: "rgba(15,15,15,0.38)",
              border: "1px solid rgba(255,255,255,0.36)",
              backdropFilter: "blur(10px)",
              "&:hover": { bgcolor: "rgba(15,15,15,0.58)" },
            }}
          >
            <ChevronLeftRoundedIcon />
          </Button>

          <Button
            type="button"
            aria-label={t("common.next")}
            onClick={() => showSlide(activeIndex + 1)}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              position: "absolute",
              right: 18,
              top: "50%",
              zIndex: 3,
              minWidth: 46,
              width: 46,
              height: 46,
              p: 0,
              borderRadius: "50%",
              color: "#fff",
              bgcolor: "rgba(15,15,15,0.38)",
              border: "1px solid rgba(255,255,255,0.36)",
              backdropFilter: "blur(10px)",
              "&:hover": { bgcolor: "rgba(15,15,15,0.58)" },
            }}
          >
            <ChevronRightRoundedIcon />
          </Button>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              zIndex: 4,
              left: "50%",
              bottom: { xs: 7, sm: 9 },
              transform: "translateX(-50%)",
              borderRadius: "999px",
              bgcolor: "rgba(15,15,15,0.34)",
              backdropFilter: "blur(10px)",
              px: 0.25,
            }}
          >
            {slides.map((slide, index) => (
              <Button
                key={slide.slot || index}
                type="button"
                aria-label={`${t("home.showHero")} ${index + 1}`}
                aria-current={index === activeIndex ? "true" : undefined}
                onClick={() => showSlide(index)}
                sx={{
                  minWidth: 44,
                  width: 44,
                  height: 44,
                  p: 0,
                  borderRadius: "50%",
                }}
              >
                <Box
                  sx={{
                    width: index === activeIndex ? 22 : 8,
                    height: 8,
                    borderRadius: "999px",
                    bgcolor:
                      index === activeIndex
                        ? c.primary
                        : "rgba(255,255,255,0.72)",
                    transition: "width 160ms ease",
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                  }}
                />
              </Button>
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  );
}

export default function Home() {
  const {
    loading,
    error,
    products = [],
    locations = [],
    categories = [],
    getCategoryName,
    bannerUrl,
    brandName,
    brandTagline,
    publicImagesReady,
    appStoreUrl,
    playStoreUrl,
    appDownloadImages = {},
    websiteContent = {},
    brandTheme,
    t,
    retryCore,
  } = useDataStore();

  const c = brandTheme || DEFAULT_BRAND_THEME;
  const content =
    websiteContent && typeof websiteContent === "object"
      ? websiteContent
      : {};
  const menu =
    content.menu && typeof content.menu === "object" ? content.menu : {};
  const locationContent =
    content.locations && typeof content.locations === "object"
      ? content.locations
      : {};
  const appContent =
    content.app && typeof content.app === "object" ? content.app : {};
  const showApp = Boolean(appStoreUrl || playStoreUrl);

  const orderedCategories = useMemo(
    () =>
      (categories || [])
        .filter(
          (category) =>
            category?.isForSale !== false &&
            safeText(category?.shortName || category?.name)
        )
        .slice()
        .sort(
          (a, b) =>
            getPriority(a) - getPriority(b) ||
            compareNames(
              a?.shortName || a?.name,
              b?.shortName || b?.name
            )
        ),
    [categories]
  );

  const curatedProducts = useMemo(() => {
    const categoryPriority = new Map(
      orderedCategories.map((category) => [
        String(category?.iref),
        getPriority(category),
      ])
    );

    return (products || [])
      .filter(
        (product) =>
          product?.isForSale !== false &&
          safeText(product?.name || product?.shortName) &&
          safeText(product?.slug || product?.iref)
      )
      .slice()
      .sort((a, b) => {
        const categoryDifference =
          (categoryPriority.get(String(a?.pCategory)) ?? 999999) -
          (categoryPriority.get(String(b?.pCategory)) ?? 999999);

        return (
          categoryDifference ||
          getPriority(a) - getPriority(b) ||
          compareNames(a?.name || a?.shortName, b?.name || b?.shortName)
        );
      })
      .slice(0, 4);
  }, [products, orderedCategories]);

  const categoryPreview = orderedCategories.slice(0, 6);
  const hasMenuDiscovery =
    Boolean(categoryPreview.length || curatedProducts.length) &&
    !loading &&
    !error;

  const heroSlides = useMemo(() => {
    const legacyHero =
      content.hero && typeof content.hero === "object"
        ? content.hero
        : {};
    const numberedHeroes = Array.isArray(content.heroes)
      ? content.heroes.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            !isExplicitlyDisabled(item.enabled)
        )
      : [];
    const candidates = numberedHeroes.length
      ? numberedHeroes
      : [legacyHero];

    const normalized = candidates
      .slice(0, 3)
      .map((item, index) => {
        const isLegacy = !numberedHeroes.length && index === 0;
        const title =
          safeText(item.title) ||
          (isLegacy
            ? safeText(brandName) || t("home.subtitle")
            : "");
        if (!title) return null;

        const primaryAction = getConfiguredAction(
          item.primaryLabel,
          item.primaryUrl
        );
        const configuredSecondaryAction = getConfiguredAction(
          item.secondaryLabel,
          item.secondaryUrl
        );
        const primaryTargetsApp = primaryAction?.to === "/app";
        const secondaryTargetsApp =
          configuredSecondaryAction?.to === "/app";

        if (
          !showApp &&
          primaryTargetsApp &&
          !configuredSecondaryAction
        ) {
          return null;
        }

        const safePrimaryAction =
          !showApp && primaryTargetsApp ? null : primaryAction;
        const safeConfiguredSecondaryAction =
          !showApp && secondaryTargetsApp
            ? null
            : configuredSecondaryAction;
        const primaryDestination = safeText(
          safePrimaryAction?.to || safePrimaryAction?.href
        );
        const secondaryAction =
          safeConfiguredSecondaryAction ||
          (hasMenuDiscovery && primaryDestination !== "/products"
            ? getConfiguredAction(t("home.viewMenu"), "/products")
            : null);

        return {
          slot: item.slot || index + 1,
          priority: Number.isFinite(Number(item.priority))
            ? Number(item.priority)
            : index + 1,
          title,
          body:
            safeText(item.body) ||
            (isLegacy
              ? safeText(brandTagline) || t("home.heroFallback")
              : ""),
          image:
            getConfiguredImage(item.image) ||
            (isLegacy ? getConfiguredImage(bannerUrl) : ""),
          mobileImage: getConfiguredImage(item.mobileImage),
          imageAlt: safeText(item.imageAlt),
          primaryAction: safePrimaryAction,
          secondaryAction,
        };
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          a.priority - b.priority ||
          Number(a.slot || 0) - Number(b.slot || 0)
      );

    if (normalized.length) return normalized.slice(0, 3);

    return [
      {
        slot: "fallback",
        priority: 1,
        title: safeText(brandName) || t("home.subtitle"),
        body:
          safeText(brandTagline) || t("home.heroFallback"),
        image: getConfiguredImage(bannerUrl),
        mobileImage: "",
        imageAlt: "",
        primaryAction: null,
        secondaryAction: hasMenuDiscovery
          ? getConfiguredAction(t("home.viewMenu"), "/products")
          : null,
      },
    ];
  }, [
    bannerUrl,
    brandName,
    brandTagline,
    content.hero,
    content.heroes,
    hasMenuDiscovery,
    showApp,
    t,
  ]);

  const configuredFeatures = useMemo(() => {
    if (!Array.isArray(content.features)) return [];

    return content.features
      .filter(
        (feature) =>
          feature &&
          typeof feature === "object" &&
          !isExplicitlyDisabled(feature.enabled) &&
          safeText(feature.title) &&
          getConfiguredImage(feature.image)
      )
      .slice(0, 2)
      .map((feature) => ({
        ...feature,
        title: safeText(feature.title),
        body: safeText(feature.body),
        image: getConfiguredImage(feature.image),
        imageAlt: safeText(feature.imageAlt),
        action: getConfiguredAction(feature.ctaLabel, feature.ctaUrl),
      }));
  }, [content.features]);

  const story = useMemo(() => {
    const configuredStory =
      content.story && typeof content.story === "object"
        ? content.story
        : null;

    if (
      !configuredStory ||
      isExplicitlyDisabled(configuredStory.enabled) ||
      !safeText(configuredStory.title) ||
      !getConfiguredImage(configuredStory.image)
    ) {
      return null;
    }

    return {
      ...configuredStory,
      title: safeText(configuredStory.title),
      body: safeText(configuredStory.body),
      image: getConfiguredImage(configuredStory.image),
      imageAlt: safeText(configuredStory.imageAlt),
      action: getConfiguredAction(
        configuredStory.ctaLabel,
        configuredStory.ctaUrl
      ),
    };
  }, [content.story]);

  const locationPreview = useMemo(
    () =>
      (locations || [])
        .filter(
          (location) =>
            safeText(location?.iref) &&
            safeText(location?.name || location?.address?.city?.name)
        )
        .slice()
        .sort((a, b) => compareNames(a?.name, b?.name))
        .slice(0, 3),
    [locations]
  );

  const appPreviewImage = publicImagesReady
    ? getConfiguredImage(appContent.image || appContent.promoImage) ||
      getConfiguredImage(appDownloadImages.screen1)
    : "";
  const appTitle =
    safeText(appContent.title) || t("appDownload.install");
  const appBody =
    safeText(appContent.body) || t("home.appDesc");

  const interactiveCardSx = {
    height: "100%",
    color: "inherit",
    textDecoration: "none",
    borderRadius: "18px",
    border: "1px solid rgba(67,20,7,0.10)",
    bgcolor: c.surface,
    overflow: "hidden",
    transition:
      "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 20px 48px rgba(33,20,12,0.13)",
      borderColor: "rgba(67,20,7,0.20)",
    },
    "&:focus-visible": {
      outline: `3px solid ${c.primary}`,
      outlineOffset: 3,
    },
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "&:hover": {
        transform: "none",
      },
    },
  };

  return (
    <Stack
      spacing={{ xs: 4.5, md: 7 }}
      sx={brandThemeCssVars(c)}
    >
      <HeroCarousel
        slides={heroSlides}
        brandName={brandName}
        brandTheme={c}
        t={t}
      />

      {error ? (
        <PageStateCard
          tone="error"
          title={t("common.loadErrorTitle")}
          description={t("common.loadError")}
          actionLabel={t("common.retry")}
          onAction={retryCore}
          brandTheme={c}
        />
      ) : loading ? (
        <PageStateCard
          loading
          title={t("common.loading")}
          brandTheme={c}
        />
      ) : null}

      {configuredFeatures.length ? (
        <Box
          component="section"
          aria-label={t("home.featuredStories")}
          sx={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md:
                configuredFeatures.length === 1
                  ? "minmax(0, 1fr)"
                  : "repeat(2, minmax(0, 1fr))",
            },
            gap: { xs: 2, md: 3 },
            alignItems: "stretch",
          }}
        >
          {configuredFeatures.map((feature, index) => {
            const single = configuredFeatures.length === 1;

            return (
              <Card
                key={safeText(feature.slot) || `${feature.title}-${index}`}
                component="article"
                elevation={0}
                sx={{
                  minWidth: 0,
                  height: "100%",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    md: single
                      ? "minmax(0, 1.15fr) minmax(320px, 0.85fr)"
                      : "minmax(0, 1fr)",
                  },
                  gridTemplateRows: {
                    xs: "auto minmax(0, 1fr)",
                    md: single
                      ? "minmax(390px, auto)"
                      : "320px minmax(0, 1fr)",
                  },
                  overflow: "hidden",
                  borderRadius: "20px",
                  border: "1px solid rgba(67,20,7,0.10)",
                  bgcolor: c.surface,
                  boxShadow: "0 18px 48px rgba(33,20,12,0.09)",
                }}
              >
                <Box
                  component={OptimizedConfigImage}
                  src={feature.image}
                  alt={feature.imageAlt}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: {
                      xs: 250,
                      sm: 300,
                      md: "100%",
                    },
                    minHeight: { md: single ? 390 : 0 },
                    maxHeight: { md: single ? 520 : "none" },
                    objectFit: "cover",
                  }}
                />

                <CardContent
                  sx={{
                    height: "100%",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: single ? "center" : "flex-start",
                    p: { xs: 2.5, sm: 3, md: single ? 4.5 : 3 },
                  }}
                >
                  <Typography
                    component="h2"
                    sx={{
                      minHeight: {
                        md: single ? 0 : "2.16em",
                      },
                      color: c.text,
                      fontWeight: 950,
                      fontSize: { xs: 27, md: single ? 40 : 31 },
                      lineHeight: 1.08,
                      letterSpacing: "-0.025em",
                      whiteSpace: "pre-line",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {feature.title}
                  </Typography>

                  {feature.body ? (
                    <Typography
                      color="text.secondary"
                      sx={{
                        mt: 1.25,
                        lineHeight: 1.65,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {feature.body}
                    </Typography>
                  ) : null}

                  {feature.action ? (
                    <Box sx={{ width: "100%", mt: "auto", pt: 2 }}>
                      <ActionButton
                        action={feature.action}
                        variant="text"
                        sx={{
                          ml: -1.25,
                          color: c.text,
                          "&:focus-visible": {
                            outline: `3px solid ${c.primary}`,
                            outlineOffset: 2,
                          },
                        }}
                      />
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : null}

      {hasMenuDiscovery ? (
        <Box
          component="section"
          aria-labelledby="home-menu-title"
        >
          <SectionHeading
            id="home-menu-title"
            title={
              safeText(menu.title) ||
              t("products.explore", t("home.viewMenu"))
            }
            body={
              safeText(menu.body) || t("home.popularCategoriesDesc")
            }
            actionLabel={t("home.allProducts")}
            actionTo="/products"
            color={c.text}
          />

          {categoryPreview.length ? (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mb: curatedProducts.length ? 2.5 : 0 }}
            >
              {categoryPreview.map((category) => {
                const label =
                  safeText(category.shortName) || safeText(category.name);
                const categoryIref = safeText(category.iref);

                return (
                  <Chip
                    key={categoryIref || label}
                    component={RouterLink}
                    to={
                      categoryIref
                        ? `/products?category=${encodeURIComponent(categoryIref)}`
                        : "/products"
                    }
                    clickable
                    icon={<LocalOfferOutlinedIcon />}
                    label={label}
                    aria-label={`${label}: ${t("home.viewProducts")}`}
                    sx={{
                      minHeight: 44,
                      borderRadius: "12px",
                      px: 0.5,
                      color: c.text,
                      bgcolor: c.surface,
                      border: "1px solid rgba(67,20,7,0.10)",
                      fontWeight: 850,
                      textDecoration: "none",
                      "& .MuiChip-icon": {
                        color: c.accent,
                      },
                      "&:hover": {
                        bgcolor: c.background,
                      },
                      "&:focus-visible": {
                        outline: `3px solid ${c.primary}`,
                        outlineOffset: 2,
                      },
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}

          {curatedProducts.length ? (
            <Grid container spacing={{ xs: 1.5, md: 2.25 }}>
              {curatedProducts.map((product) => {
                const image = getMainImage(
                  product,
                  "productImage_product"
                );
                const title =
                  safeText(product.name) || safeText(product.shortName);
                const categoryName = product.pCategory
                  ? getCategoryName?.(product.pCategory)
                  : "";

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={safeText(product.iref || product.slug)}
                  >
                    <Card
                      component={RouterLink}
                      to={`/products/${product.slug || product.iref}`}
                      elevation={0}
                      aria-label={`${title}: ${t("common.viewDetails")}`}
                      sx={interactiveCardSx}
                    >
                      <Box
                        sx={{
                          height: { xs: 230, sm: 210, md: 230 },
                          bgcolor: c.background,
                          overflow: "hidden",
                        }}
                      >
                        {image ? (
                          <Box
                            component="img"
                            src={withImageSize(image, "l")}
                            alt={title}
                            loading="lazy"
                            decoding="async"
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "block",
                              objectFit: "cover",
                              transition: "transform 240ms ease",
                              ".MuiCard-root:hover &": {
                                transform: "scale(1.025)",
                              },
                              "@media (prefers-reduced-motion: reduce)": {
                                transition: "none",
                                ".MuiCard-root:hover &": {
                                  transform: "none",
                                },
                              },
                            }}
                          />
                        ) : (
                          <BrandedImageFallback
                            icon={<RestaurantOutlinedIcon />}
                            label={title || t("common.noImage")}
                            brandTheme={c}
                            sx={{ minHeight: 0 }}
                          />
                        )}
                      </Box>

                      <CardContent sx={{ p: 2 }}>
                        {categoryName ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mb: 0.6,
                              color: c.accent,
                              fontWeight: 900,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                            }}
                          >
                            {categoryName}
                          </Typography>
                        ) : null}

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography
                            component="h3"
                            sx={{
                              minWidth: 0,
                              color: c.text,
                              fontWeight: 900,
                              fontSize: 17,
                              lineHeight: 1.3,
                            }}
                          >
                            {title}
                          </Typography>
                          <ArrowForwardRoundedIcon
                            aria-hidden="true"
                            sx={{ flexShrink: 0, color: c.accent }}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : categoryPreview.length ? (
            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {categoryPreview.slice(0, 4).map((category) => {
                const image = getCategoryImage(category);
                const title =
                  safeText(category.shortName) || safeText(category.name);
                const categoryIref = safeText(category.iref);

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={safeText(category.iref) || title}
                  >
                    <Card
                      component={RouterLink}
                      to={
                        categoryIref
                          ? `/products?category=${encodeURIComponent(categoryIref)}`
                          : "/products"
                      }
                      elevation={0}
                      aria-label={`${title}: ${t("home.viewProducts")}`}
                      sx={interactiveCardSx}
                    >
                      <Box sx={{ height: 210, bgcolor: c.background }}>
                        {image ? (
                          <Box
                            component="img"
                            src={withImageSize(image, "l")}
                            alt={title}
                            loading="lazy"
                            decoding="async"
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "block",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <BrandedImageFallback
                            icon={<LocalOfferOutlinedIcon />}
                            label={title || t("common.noImage")}
                            brandTheme={c}
                            sx={{ minHeight: 0 }}
                          />
                        )}
                      </Box>
                      <CardContent sx={{ p: 2 }}>
                        <Typography
                          component="h3"
                          sx={{ color: c.text, fontWeight: 900 }}
                        >
                          {title}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : null}
        </Box>
      ) : null}

      {story ? (
        <Box
          id="story"
          component="section"
          aria-labelledby="home-story-title"
          sx={{
            scrollMarginTop: { xs: 88, md: 104 },
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) minmax(340px, 0.9fr)",
            },
            overflow: "hidden",
            borderRadius: { xs: "18px", md: "24px" },
            bgcolor: c.dark,
            boxShadow: "0 24px 64px rgba(23,17,12,0.18)",
          }}
        >
          <Box
            component={OptimizedConfigImage}
            src={story.image}
            alt={story.imageAlt}
            loading="lazy"
            decoding="async"
            sx={{
              width: "100%",
              height: "100%",
              minHeight: { xs: 290, md: 470 },
              maxHeight: 560,
              objectFit: "cover",
            }}
          />

          <Stack
            alignItems="flex-start"
            justifyContent="center"
            sx={{ p: { xs: 3, sm: 4, md: 6 } }}
          >
            <Typography
              component="h2"
              id="home-story-title"
              sx={{
                color: "#ffffff",
                fontWeight: 950,
                fontSize: { xs: 32, sm: 40, md: 48 },
                lineHeight: 1.04,
                letterSpacing: "-0.035em",
                whiteSpace: "pre-line",
                overflowWrap: "anywhere",
              }}
            >
              {story.title}
            </Typography>

            {story.body ? (
              <Typography
                sx={{
                  mt: 1.75,
                  color: "rgba(255,255,255,0.78)",
                  fontSize: { xs: 15, md: 17 },
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                {story.body}
              </Typography>
            ) : null}

            {story.action ? (
              <ActionButton
                action={story.action}
                variant="outlined"
                sx={{
                  mt: 2.75,
                  color: "#ffffff",
                  borderColor: "rgba(255,255,255,0.48)",
                  "&:hover": {
                    color: "#ffffff",
                    borderColor: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                  "&:focus-visible": {
                    outline: `3px solid ${c.primary}`,
                    outlineOffset: 3,
                  },
                }}
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {!loading && !error && locationPreview.length ? (
        <Box
          component="section"
          aria-labelledby="home-locations-title"
        >
          <SectionHeading
            id="home-locations-title"
            title={
              safeText(locationContent.title) ||
              t("home.nearestLocation")
            }
            body={
              safeText(locationContent.body) ||
              t("home.locationsDesc")
            }
            actionLabel={t("home.viewLocations")}
            actionTo="/locations"
            color={c.text}
          />

          <Grid container spacing={{ xs: 1.5, md: 2.25 }}>
            {locationPreview.map((location) => {
              const image = getMainImage(
                location,
                "locationImage_location"
              );
              const title =
                safeText(location.name) || t("locations.location");
              const addressLine = getAddressLine(location);

              return (
                <Grid
                  item
                  xs={12}
                  md={4}
                  key={safeText(location.iref)}
                >
                  <Card
                    component={RouterLink}
                    to={`/locations/${location.iref}`}
                    elevation={0}
                    aria-label={`${title}: ${t("common.viewDetails")}`}
                    sx={interactiveCardSx}
                  >
                    <Box
                      sx={{
                        height: { xs: 225, md: 240 },
                        bgcolor: c.background,
                      }}
                    >
                      {image ? (
                        <Box
                          component="img"
                          src={withImageSize(image, "l")}
                          alt={title}
                          loading="lazy"
                          decoding="async"
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "block",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <BrandedImageFallback
                          icon={<LocationOnOutlinedIcon />}
                          label={title || t("common.noImage")}
                          brandTheme={c}
                          sx={{ minHeight: 0 }}
                        />
                      )}
                    </Box>

                    <CardContent sx={{ p: 2.25 }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            component="h3"
                            sx={{
                              color: c.text,
                              fontWeight: 900,
                              fontSize: 19,
                              lineHeight: 1.3,
                            }}
                          >
                            {title}
                          </Typography>

                          {addressLine ? (
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="flex-start"
                              sx={{ mt: 0.8 }}
                            >
                              <LocationOnOutlinedIcon
                                aria-hidden="true"
                                sx={{
                                  mt: "2px",
                                  flexShrink: 0,
                                  color: c.accent,
                                  fontSize: 18,
                                }}
                              />
                              <Typography
                                color="text.secondary"
                                sx={{ lineHeight: 1.5 }}
                              >
                                {addressLine}
                              </Typography>
                            </Stack>
                          ) : null}
                        </Box>
                        <ArrowForwardRoundedIcon
                          aria-hidden="true"
                          sx={{ flexShrink: 0, color: c.accent }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ) : null}

      {showApp ? (
        <Box
          component="section"
          aria-labelledby="home-app-title"
          sx={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: appPreviewImage
                ? "minmax(0, 1.15fr) minmax(280px, 0.65fr)"
                : "1fr",
            },
            overflow: "hidden",
            borderRadius: { xs: "18px", md: "24px" },
            bgcolor: c.dark,
            background: `linear-gradient(125deg, ${c.dark} 0%, ${c.accent} 160%)`,
            boxShadow: "0 24px 64px rgba(23,17,12,0.18)",
          }}
        >
          <Stack
            alignItems="flex-start"
            justifyContent="center"
            sx={{
              position: "relative",
              zIndex: 1,
              p: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Box
              aria-hidden="true"
              sx={{
                width: 48,
                height: 48,
                mb: 2,
                display: "grid",
                placeItems: "center",
                borderRadius: "14px",
                color: c.onPrimary,
                bgcolor: c.primary,
              }}
            >
              <PhoneIphoneRoundedIcon />
            </Box>

            <Typography
              component="h2"
              id="home-app-title"
              sx={{
                maxWidth: 680,
                color: "#ffffff",
                fontWeight: 950,
                fontSize: { xs: 31, sm: 39, md: 46 },
                lineHeight: 1.06,
                letterSpacing: "-0.03em",
                whiteSpace: "pre-line",
                overflowWrap: "anywhere",
              }}
            >
              {appTitle}
            </Typography>

            {appBody ? (
              <Typography
                sx={{
                  mt: 1.5,
                  maxWidth: 600,
                  color: "rgba(255,255,255,0.78)",
                  fontSize: { xs: 15, md: 17 },
                  lineHeight: 1.65,
                  whiteSpace: "pre-line",
                }}
              >
                {appBody}
              </Typography>
            ) : null}

            <Button
              component={RouterLink}
              to="/app"
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                mt: 2.75,
                minHeight: 46,
                borderRadius: "12px",
                px: 2.5,
                color: c.onPrimary,
                bgcolor: c.primary,
                fontWeight: 900,
                textTransform: "none",
                "&:hover": {
                  color: c.onPrimary,
                  bgcolor: c.primary,
                  filter: "brightness(0.94)",
                },
                "&:focus-visible": {
                  outline: "3px solid #ffffff",
                  outlineOffset: 3,
                },
              }}
            >
              {t("home.downloadApp")}
            </Button>
          </Stack>

          {appPreviewImage ? (
            <Box
              sx={{
                position: "relative",
                minHeight: { xs: 280, md: 390 },
                overflow: "hidden",
              }}
            >
              <HomeAppScreen
                src={appPreviewImage}
                alt={
                  brandName
                    ? `${t("home.appAlt")} ${brandName}`
                    : t("home.appAlt")
                }
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );
}
